import React, { useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, FileText, Upload } from 'lucide-react';
import {
  LEGAL_CATEGORIES_BY_AUDIENCE,
  LEGAL_CATEGORY_LABELS,
  LEGAL_POLICY_SCOPED_ALL,
  readMarkdownFileAsText,
  createLegalPolicyVersion,
  type LegalAudience,
  type LegalCategory,
  type CreateLegalPolicyPayload,
} from '../../services/MasterAdmin/legalPolicyVersionsService';
import { vendorRoleMapping } from '../../services/MasterAdmin/vendorService';

const LegalPolicyCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const [audience, setAudience] = useState<LegalAudience>('USER');
  const [category, setCategory] = useState<LegalCategory>(
    LEGAL_CATEGORIES_BY_AUDIENCE.USER[0]
  );
  const [scoped, setScoped] = useState<number>(LEGAL_POLICY_SCOPED_ALL);

  const categoryOptions = useMemo<LegalCategory[]>(
    () => LEGAL_CATEGORIES_BY_AUDIENCE[audience],
    [audience]
  );

  const handleAudienceChange = (next: LegalAudience) => {
    setAudience(next);
    const validCategories = LEGAL_CATEGORIES_BY_AUDIENCE[next];
    if (!validCategories.includes(category)) {
      setCategory(validCategories[0]);
    }
  };
  const [ptitle, setPtitle] = useState('');
  const [markdown, setMarkdown] = useState('');
  const [publishNow, setPublishNow] = useState(false);
  /** Name of last .md imported into the textarea (for feedback only). */
  const [markdownImportLabel, setMarkdownImportLabel] = useState<string | null>(null);
  const markdownFileInputRef = useRef<HTMLInputElement | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const vendorTypeOptions = useMemo(() => {
    return [
      { value: LEGAL_POLICY_SCOPED_ALL, label: 'All vendor types' },
      ...Object.entries(vendorRoleMapping).map(([value, label]) => ({
        value: Number(value),
        label,
      })),
    ];
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!markdown.trim()) {
      setError('Enter markdown content or upload a .md file.');
      return;
    }
    const payload: CreateLegalPolicyPayload = {
      audience,
      category,
      scopedVendorType: audience === 'VENDOR' ? scoped : LEGAL_POLICY_SCOPED_ALL,
      title: ptitle || undefined,
      markdownContent: markdown,
      publishNow,
    };
    setBusy(true);
    try {
      await createLegalPolicyVersion(payload, null);
      navigate('/operations/legal-policies');
    } catch (e: unknown) {
      const msg =
        typeof e === 'object' &&
        e !== null &&
        'response' in e &&
        typeof (e as { response?: { data?: { message?: string } } }).response?.data?.message === 'string'
          ? String((e as { response?: { data?: { message?: string } } }).response?.data?.message)
          : 'Failed to create policy version.';
      setError(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="w-full min-w-0 max-w-3xl mx-auto space-y-6 p-3 sm:p-4 md:p-6">
      <div className="flex flex-wrap items-center gap-3">
        <Link
          to="/operations/legal-policies"
          className="inline-flex items-center gap-2 text-sm font-medium text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to list
        </Link>
      </div>

      <div className="flex items-start gap-3">
        <FileText className="mt-1 h-6 w-6 shrink-0 text-blue-600 dark:text-blue-400" />
        <div>
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">Create policy version</h1>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
            New versions are drafts until you publish. Vendor policies can be scoped by vendor type.
          </p>
        </div>
      </div>

      <form
        onSubmit={(e) => void handleSubmit(e)}
        className="space-y-4 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800 md:p-6"
      >
        {error && (
          <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900/40 dark:bg-red-900/20 dark:text-red-300">
            {error}
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Audience</label>
          <select
            value={audience}
            onChange={(e) => handleAudienceChange(e.target.value as LegalAudience)}
            className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-950 dark:text-white"
          >
            <option value="USER">User</option>
            <option value="VENDOR">Vendor</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Category</label>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value as LegalCategory)}
            className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-950 dark:text-white"
          >
            {categoryOptions.map((c) => (
              <option key={c} value={c}>
                {LEGAL_CATEGORY_LABELS[c]}
              </option>
            ))}
          </select>
        </div>

        {audience === 'VENDOR' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Vendor type scope</label>
            <select
              value={scoped}
              onChange={(e) => setScoped(Number(e.target.value))}
              className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-950 dark:text-white"
            >
              {vendorTypeOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Title (optional)</label>
          <input
            value={ptitle}
            onChange={(e) => setPtitle(e.target.value)}
            className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-950 dark:text-white"
            placeholder="e.g. May 2026 — User Terms"
          />
        </div>

        <div>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Markdown body</label>
            <label className="inline-flex cursor-pointer items-center gap-1 rounded-lg border border-gray-200 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-800">
              <Upload className="h-3.5 w-3.5" />
              Upload .md
              <input
                ref={markdownFileInputRef}
                type="file"
                accept=".md,text/markdown,text/plain,.markdown"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = '';
                  if (!f) return;
                  void readMarkdownFileAsText(f)
                    .then((text: string) => {
                      setMarkdown(text);
                      setMarkdownImportLabel(f.name);
                      setError(null);
                    })
                    .catch(() => {
                      setError('Could not read the file. Try a UTF-8 .md file.');
                    });
                }}
              />
            </label>
            {markdownImportLabel && (
              <button
                type="button"
                className="text-xs font-medium text-gray-600 underline-offset-2 hover:underline dark:text-gray-400"
                onClick={() => {
                  setMarkdownImportLabel(null);
                  if (markdownFileInputRef.current) markdownFileInputRef.current.value = '';
                }}
              >
                Clear import label
              </button>
            )}
          </div>
          {markdownImportLabel && (
            <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
              Loaded into editor: <span className="font-medium">{markdownImportLabel}</span>. You can edit below before
              saving.
            </p>
          )}
          <textarea
            value={markdown}
            onChange={(e) => setMarkdown(e.target.value)}
            rows={16}
            className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 font-mono text-xs leading-relaxed dark:border-gray-600 dark:bg-gray-950 dark:text-white"
            placeholder="# Heading&#10;Write markdown here, or upload a .md file."
          />
        </div>

        <label className="flex items-center gap-2 text-sm text-gray-800 dark:text-gray-200">
          <input type="checkbox" checked={publishNow} onChange={(e) => setPublishNow(e.target.checked)} />
          Publish immediately
        </label>

        <div className="flex flex-wrap justify-end gap-2 border-t border-gray-100 pt-4 dark:border-gray-700">
          <Link
            to="/operations/legal-policies"
            className="inline-flex rounded-xl border border-gray-200 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700/50"
          >
            Cancel
          </Link>
          <button
            type="submit"
            disabled={busy || !markdown.trim()}
            className="rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {busy ? 'Creating…' : 'Create draft'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default LegalPolicyCreatePage;
